import { FC, useCallback, useEffect, useState } from 'react'
import type {
  CrmChatProviderKey,
  CrmLeadIntegrationChatTab,
} from '@/api/types/crm/crmLeadFunnels.types'
import { isCrmIntegrationTextChatProvider } from '../../lib/crmChatProviderKey'
import CrmIntegrationChatsList from '../CrmIntegrationChatsList/CrmIntegrationChatsList'
import CrmIntegrationTextChat from '../CrmIntegrationTextChat/CrmIntegrationTextChat'

const EMPTY_COMMUNITY_TABS: CrmLeadIntegrationChatTab[] = []

type IntegrationChatScreen = 'conversation' | 'chats-list'

export type CrmIntegrationChatProps = {
  provider: CrmChatProviderKey
  leadId: number
  conversationId?: number | null
  communityTabs?: CrmLeadIntegrationChatTab[]
  variant?: 'modal' | 'dock' | 'page'
  enabled?: boolean
  onDismiss?: () => void
  onCollapse?: () => void
  onBackFromConversation?: () => void
  onMarkReadSuccess?: (leadId: number, conversationId?: number) => void
  onSummaryChange?: () => void
  onLeadSelect?: (
    leadId: number,
    options?: { conversationId?: number | null },
  ) => void
  onConversationIdChange?: (conversationId: number) => void
  /** Modal: список чатов внутри модалки. */
  screen?: IntegrationChatScreen
  onScreenChange?: (screen: IntegrationChatScreen) => void
}

const CrmIntegrationChat: FC<CrmIntegrationChatProps> = ({
  provider,
  leadId,
  conversationId = null,
  communityTabs = EMPTY_COMMUNITY_TABS,
  variant = 'dock',
  enabled = true,
  onDismiss,
  onCollapse,
  onBackFromConversation,
  onMarkReadSuccess,
  onLeadSelect,
  onConversationIdChange,
  screen: controlledScreen,
  onScreenChange,
}) => {
  const [internalScreen, setInternalScreen] =
    useState<IntegrationChatScreen>('conversation')
  const [activeLeadId, setActiveLeadId] = useState(leadId)
  const [activeConversationId, setActiveConversationId] = useState<number | null>(
    conversationId,
  )
  const [activeCommunityTabs, setActiveCommunityTabs] =
    useState<CrmLeadIntegrationChatTab[]>(communityTabs)

  const isScreenControlled = onScreenChange != null
  const screen = isScreenControlled
    ? controlledScreen ?? 'conversation'
    : internalScreen

  const changeScreen = useCallback(
    (next: IntegrationChatScreen) => {
      if (isScreenControlled) {
        onScreenChange?.(next)
        return
      }
      setInternalScreen(next)
    },
    [isScreenControlled, onScreenChange],
  )

  useEffect(() => {
    setActiveLeadId(leadId)
  }, [leadId])

  useEffect(() => {
    setActiveConversationId(conversationId ?? null)
  }, [conversationId])

  useEffect(() => {
    setActiveCommunityTabs(communityTabs)
  }, [communityTabs])

  useEffect(() => {
    if (variant === 'modal' && enabled && !isScreenControlled) {
      setInternalScreen('conversation')
    }
  }, [enabled, isScreenControlled, leadId, variant])

  const handleConversationIdChange = useCallback(
    (nextConversationId: number) => {
      setActiveConversationId(nextConversationId)
      onConversationIdChange?.(nextConversationId)
    },
    [onConversationIdChange],
  )

  const handleBackFromConversation = useCallback(() => {
    if ((variant === 'dock' || variant === 'page') && onBackFromConversation) {
      onBackFromConversation()
      return
    }
    if (variant === 'modal') {
      changeScreen('chats-list')
      return
    }
    onBackFromConversation?.()
  }, [changeScreen, onBackFromConversation, variant])

  if (
    screen === 'chats-list' &&
    variant !== 'dock' &&
    variant !== 'page' &&
    isCrmIntegrationTextChatProvider(provider)
  ) {
    return (
      <CrmIntegrationChatsList
        provider={provider}
        variant={variant}
        onDismiss={onDismiss}
        onCollapse={onCollapse}
        onSelectChat={(selection) => {
          if (selection.leadId != null) {
            setActiveLeadId(selection.leadId)
            setActiveCommunityTabs(EMPTY_COMMUNITY_TABS)
            onLeadSelect?.(selection.leadId, {
              conversationId: selection.conversationId,
            })
          }
          setActiveConversationId(selection.conversationId)
          onConversationIdChange?.(selection.conversationId)
          changeScreen('conversation')
        }}
      />
    )
  }

  if (isCrmIntegrationTextChatProvider(provider)) {
    return (
      <CrmIntegrationTextChat
        provider={provider}
        leadId={activeLeadId}
        conversationId={activeConversationId}
        communityTabs={activeCommunityTabs}
        variant={variant}
        enabled={enabled}
        onDismiss={onDismiss}
        onCollapse={onCollapse}
        onBack={handleBackFromConversation}
        onConversationIdChange={handleConversationIdChange}
        onMarkReadSuccess={onMarkReadSuccess}
      />
    )
  }

  return null
}

export default CrmIntegrationChat
